import React, { useEffect, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { Button } from "@/components/ui/button";
import {
  Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2,
  Link as LinkIcon, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight,
  Undo2, Redo2, Code2, Eye, Quote,
} from "lucide-react";

function ToolbarBtn({ active, onClick, title, children, testid }) {
  return (
    <button type="button" title={title} onClick={onClick} data-testid={testid}
      className={`p-1.5 rounded hover:bg-muted transition ${active ? "bg-muted text-primary" : "text-foreground/80"}`}>
      {children}
    </button>
  );
}

/**
 * TipTap-based Rich-Text editor met een raw-HTML toggle.
 * value / onChange werken op HTML-strings zodat het naadloos aansluit op de
 * bestaande e-mail-template flow (render_template op de backend).
 */
export default function RichTextEditor({ value = "", onChange, minHeight = 260, testid = "rte" }) {
  const [rawMode, setRawMode] = useState(false);
  const [rawHtml, setRawHtml] = useState(value);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-primary underline" } }),
      Image,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value || "<p></p>",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      setRawHtml(html);
      onChange?.(html);
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none px-4 py-3 min-h-[220px]",
        "data-testid": `${testid}-content`,
      },
    },
  });

  // Wanneer de parent-value van buitenaf verandert (bv. template geladen),
  // sync die naar de editor toe.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value && value !== current) {
      editor.commands.setContent(value, false);
      setRawHtml(value);
    }
  }, [value, editor]);

  if (!editor) return <div className="border rounded-lg h-64 animate-pulse bg-muted" />;

  const addLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };
  const addImage = () => {
    const url = window.prompt("Afbeeldings-URL", "https://");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };
  const toggleRaw = () => {
    if (rawMode) {
      // van raw → editor: sync
      editor.commands.setContent(rawHtml || "<p></p>", false);
      onChange?.(rawHtml);
    }
    setRawMode(m => !m);
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-background" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 border-b px-2 py-1.5 bg-muted/40">
        <ToolbarBtn testid="rte-bold" active={editor.isActive("bold")} title="Vet"
          onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn testid="rte-italic" active={editor.isActive("italic")} title="Cursief"
          onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn testid="rte-strike" active={editor.isActive("strike")} title="Doorhalen"
          onClick={() => editor.chain().focus().toggleStrike().run()}><Underline className="h-3.5 w-3.5" /></ToolbarBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn testid="rte-h1" active={editor.isActive("heading", { level: 1 })} title="Kop 1"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn testid="rte-h2" active={editor.isActive("heading", { level: 2 })} title="Kop 2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn testid="rte-quote" active={editor.isActive("blockquote")} title="Quote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="h-3.5 w-3.5" /></ToolbarBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn testid="rte-ul" active={editor.isActive("bulletList")} title="Lijst"
          onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn testid="rte-ol" active={editor.isActive("orderedList")} title="Genummerd"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="h-3.5 w-3.5" /></ToolbarBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn testid="rte-align-left" active={editor.isActive({ textAlign: "left" })} title="Links"
          onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn testid="rte-align-center" active={editor.isActive({ textAlign: "center" })} title="Midden"
          onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn testid="rte-align-right" active={editor.isActive({ textAlign: "right" })} title="Rechts"
          onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight className="h-3.5 w-3.5" /></ToolbarBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn testid="rte-link" active={editor.isActive("link")} title="Link" onClick={addLink}><LinkIcon className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn testid="rte-image" title="Afbeelding" onClick={addImage}><ImageIcon className="h-3.5 w-3.5" /></ToolbarBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn testid="rte-undo" title="Ongedaan maken" onClick={() => editor.chain().focus().undo().run()}><Undo2 className="h-3.5 w-3.5" /></ToolbarBtn>
        <ToolbarBtn testid="rte-redo" title="Opnieuw" onClick={() => editor.chain().focus().redo().run()}><Redo2 className="h-3.5 w-3.5" /></ToolbarBtn>
        <div className="flex-1" />
        <Button type="button" variant="ghost" size="sm" onClick={toggleRaw} data-testid="rte-toggle-raw"
          className="h-7 text-[11px]">
          {rawMode ? <><Eye className="h-3.5 w-3.5 mr-1" />Editor</> : <><Code2 className="h-3.5 w-3.5 mr-1" />HTML</>}
        </Button>
      </div>
      {rawMode ? (
        <textarea
          className="w-full font-mono text-xs bg-background outline-none px-3 py-2"
          style={{ minHeight }}
          value={rawHtml}
          data-testid="rte-raw-textarea"
          onChange={(e) => { setRawHtml(e.target.value); onChange?.(e.target.value); }}
        />
      ) : (
        <div style={{ minHeight }} className="tiptap-container">
          <EditorContent editor={editor} />
        </div>
      )}
    </div>
  );
}
